import { useEffect, useState } from "react";
import Box from "elements/Box";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Pagination,
  Tooltip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CodeIcon from "@mui/icons-material/Code";
import SearchIcon from "@mui/icons-material/Search";
import { ProjectConfig } from "Global";

const statusColors: any = {
  pending: "warning",
  sent: "info",
  accepted: "success",
  rejected: "error",
  error: "error",
};

const statusLabels: any = {
  pending: "Pendiente",
  sent: "Enviado",
  accepted: "Aceptado",
  rejected: "Rechazado",
  error: "Error",
};

const docTypeLabels: any = {
  invoice: "Factura",
  credit_note: "Nota Crédito",
  debit_note: "Nota Débito",
};

export default function DianDocuments(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [documents, setDocuments] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    document_type: "",
    status: "",
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    setLoading(false);
    loadDocuments();
  }, [page]);

  const loadDocuments = () => {
    const query: any = { page };
    if (filters.document_type) query.document_type = filters.document_type;
    if (filters.status) query.status = filters.status;
    if (filters.start_date) query.start_date = filters.start_date;
    if (filters.end_date) query.end_date = filters.end_date;

    ApiRequest({
      url: "/dian/documents/list",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query,
      setResponse: (response: any) => {
        if (response.list) {
          setDocuments(response.list);
          setTotalPages(response.total_pages || 1);
        }
      },
    });
  };

  const handleFilterChange = (e: any) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    setPage(1);
    loadDocuments();
  };

  const retryDocument = (documentId: number) => {
    ApiRequest({
      url: "/dian/document/retry",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: { document_id: documentId },
      setResponse: (response: any) => {
        if (response.success) {
          enqueueSnackbar("Documento reenviado exitosamente", { variant: "success" });
          loadDocuments();
        } else {
          enqueueSnackbar(response.error || "Error al reenviar", { variant: "error" });
        }
      },
    });
  };

  const checkStatus = (documentId: number) => {
    ApiRequest({
      url: "/dian/document/status",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query: { document_id: documentId },
      setResponse: (response: any) => {
        enqueueSnackbar(`Estado: ${statusLabels[response.status] || response.status}`, {
          variant: response.status === "accepted" ? "success" : "info",
        });
        loadDocuments();
      },
    });
  };

  const downloadPdf = (doc: any) => {
    if (doc.pdf_url) window.open(`${ProjectConfig.api_url}${doc.pdf_url}`, "_blank");
  };

  const downloadXml = (documentId: number) => {
    const ltkn = localStorage.getItem("ltkn");
    window.open(`${ProjectConfig.api_url}/dian/document/xml?document_id=${documentId}&ltkn=${ltkn}`, "_blank");
  };

  return (
    <div id="DianDocuments" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Documentos Electrónicos</h2>
            <span className="h2_subtitle">Historial de documentos enviados a la DIAN</span>
          </div>
        </div>

        {/* Filters */}
        <Box id="dianFilters" className="box100">
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tipo</InputLabel>
              <Select name="document_type" value={filters.document_type} onChange={handleFilterChange} label="Tipo">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="invoice">Factura</MenuItem>
                <MenuItem value="credit_note">Nota Crédito</MenuItem>
                <MenuItem value="debit_note">Nota Débito</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Estado</InputLabel>
              <Select name="status" value={filters.status} onChange={handleFilterChange} label="Estado">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="sent">Enviado</MenuItem>
                <MenuItem value="accepted">Aceptado</MenuItem>
                <MenuItem value="rejected">Rechazado</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Fecha inicio"
              name="start_date"
              type="date"
              size="small"
              value={filters.start_date}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Fecha fin"
              name="end_date"
              type="date"
              size="small"
              value={filters.end_date}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" startIcon={<SearchIcon />} onClick={applyFilters}>
              Buscar
            </Button>
          </div>
        </Box>

        {/* Documents Table */}
        <Box id="dianDocTable" className="box100">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Adquiriente</TableCell>
                <TableCell>NIT</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((doc: any) => (
                <TableRow key={doc.document_id}>
                  <TableCell>
                    <strong>
                      {doc.prefix}
                      {doc.number}
                    </strong>
                  </TableCell>
                  <TableCell>{docTypeLabels[doc.document_type] || doc.document_type}</TableCell>
                  <TableCell>{doc.buyer_name}</TableCell>
                  <TableCell>{doc.buyer_nit}</TableCell>
                  <TableCell align="right">
                    {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(
                      doc.payable_amount || 0
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[doc.status] || doc.status}
                      color={statusColors[doc.status] || "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{doc.issue_date ? new Date(doc.issue_date).toLocaleDateString("es-CO") : ""}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Consultar estado DIAN">
                      <IconButton size="small" onClick={() => checkStatus(doc.document_id)}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(doc.status === "error" || doc.status === "rejected") && (
                      <Tooltip title="Reintentar envío">
                        <IconButton size="small" color="warning" onClick={() => retryDocument(doc.document_id)}>
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {doc.pdf_path && (
                      <Tooltip title="Descargar PDF">
                        <IconButton size="small" color="error" onClick={() => downloadPdf(doc)}>
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Descargar XML">
                      <IconButton size="small" color="primary" onClick={() => downloadXml(doc.document_id)}>
                        <CodeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {documents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No hay documentos electrónicos
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
            </div>
          )}
        </Box>
      </div>
    </div>
  );
}
